import type { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { type SseFrame, readSseFrames } from '@/lib/streaming/sse';

export type DealItemsBatch = {
  items: DealItemWithVersions[];
  isLastBatch: boolean;
};

export class DealItemsStreamError extends Error {
  readonly status?: number;
  readonly body?: string;

  constructor(message: string, status?: number, body?: string) {
    super(message);
    this.name = 'DealItemsStreamError';
    this.status = status;
    this.body = body || undefined;
  }
}

type StreamDealItemsOptions = {
  baseUrl: string;
  dealId: string;
  token: string;
  signal: AbortSignal;
  onBatch: (batch: DealItemsBatch) => void;
  /**
   * Restreint la lecture à ces items : la requête passe en `POST` avec les ids
   * dans le corps JSON (`DealItemsStreamBody`, cf. `lib/deal-events.types.ts`).
   * Omis ou vide, la lecture reste un `GET` et le serveur renvoie toute la
   * version active — c'est le chargement complet.
   *
   * Les ids voyagent dans le corps et non dans l'URL : quelques centaines
   * suffisaient à dépasser ce qu'un en-tête de requête HTTP peut porter (Node
   * refuse au-delà de 16 Kio, token compris) et la lecture n'a rien à gagner à
   * être cachable, elle suit un signal de changement.
   *
   * Le serveur se réserve d'ignorer le filtre (liste trop longue) et de renvoyer
   * quand même tout : un appelant ne peut donc pas supposer que la réponse se limite
   * aux ids demandés, seulement qu'elle les contient.
   */
  itemIds?: string[];
};

export function dealItemsStreamUrl(baseUrl: string, dealId: string): string {
  return `${baseUrl}/deals/${dealId}/items/stream`;
}

function dealItemsStreamRequest(
  token: string,
  signal: AbortSignal,
  itemIds?: string[]
): RequestInit {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'text/event-stream',
  };
  if (!itemIds?.length) return { method: 'GET', headers, signal };
  headers['Content-Type'] = 'application/json';
  return {
    method: 'POST',
    headers,
    body: JSON.stringify({ itemIds }),
    signal,
  };
}

export async function streamDealItems({
  baseUrl,
  dealId,
  token,
  signal,
  onBatch,
  itemIds,
}: StreamDealItemsOptions): Promise<void> {
  const response = await fetch(
    dealItemsStreamUrl(baseUrl, dealId),
    dealItemsStreamRequest(token, signal, itemIds)
  );

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new DealItemsStreamError(
      `Deal items stream failed with status ${response.status}`,
      response.status,
      body
    );
  }
  if (!response.body) {
    throw new DealItemsStreamError('Deal items stream returned an empty body');
  }

  let sawLastBatch = false;

  const handleFrame = (frame: SseFrame): void => {
    if (frame.event === 'error') {
      throw new DealItemsStreamError(
        'Deal items stream emitted an error event',
        undefined,
        frame.data
      );
    }
    if (!frame.data) return;
    let batch: DealItemsBatch;
    try {
      batch = JSON.parse(frame.data) as DealItemsBatch;
    } catch {
      throw new DealItemsStreamError(
        'Deal items stream sent a malformed data frame'
      );
    }
    if (batch.isLastBatch) sawLastBatch = true;
    onBatch(batch);
  };

  try {
    await readSseFrames(response.body, handleFrame);
    if (!signal.aborted && !sawLastBatch) {
      throw new DealItemsStreamError(
        'Deal items stream ended before the last batch'
      );
    }
  } catch (error) {
    if (signal.aborted) return;
    throw error;
  }
}
